import { Redirect } from '@change-corgi/core/react/router';

import { withPrefetchedData } from 'src/shared/prefetch';
import type { PrefetchedDataProps } from 'src/shared/prefetch';

import { isError, isLoading } from 'src/app/shared/utils/async';

import { BannerContainer } from './BannerContainer';
import { prefetchPetitionInfo, usePetitionInfo } from './hooks/petitionInfo';
import type { PetitionInfoState } from './hooks/petitionInfo';

type Props = Readonly<{
	slug: string;
}>;
type PrefetchedData = { info: PetitionInfoState };

/**
 * This component originally came from a shared header that was displayed on multiple Old Petition Page routes. Now it
 * is only used by Petition Gamma to provide the BannerContainer with extra data. The banner should just take over this
 * data fetching, which could be refactored use the PageContext. (The old page had a mix of old/new methods for fetching
 * user & petition data, but that shouldn't be the approach anymore.)
 */
export const BannerSection = withPrefetchedData(
	function BannerSection({ slug, prefetchedData }: Props & PrefetchedDataProps<PrefetchedData>) {
		const petitionInfoState = usePetitionInfo(slug, prefetchedData?.info);

		// TODO use a fixed height or a ghost to avoid layout shifting?
		if (isLoading(petitionInfoState)) return null;

		if (isError(petitionInfoState) && petitionInfoState.reason === 'notFound') {
			return <Redirect to="/?petition_not_found=true" />;
		}

		if (isError(petitionInfoState)) {
			throw new Error('Petition Header: Failed to load petition details', { cause: petitionInfoState.cause });
		}

		return (
			<BannerContainer
				petitionId={petitionInfoState.info.id}
				emergencyBanner={petitionInfoState.info.emergencyBanner}
			/>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [BannerContainer],
		prefetchData: async (context) => {
			const {
				params: { slug },
				utilityContext,
			} = context;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			return { info: await prefetchPetitionInfo(slug!, utilityContext) };
		},
	},
);
